/**
 * Utility functions for converting between HTML and plain text in email editing
 */

/**
 * Convert plain text to HTML, preserving line breaks and basic formatting
 * @param {string} text - Plain text string
 * @returns {string} HTML string
 */
export const textToHtml = (text) => {
  if (!text) return '';
  
  return text
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n/g, '<br>')
    .trim();
};

/**
 * Convert HTML to plain text for email sending
 * @param {string} html - HTML string
 * @returns {string} Plain text string
 */
export const htmlToText = (html) => {
  if (!html) return '';
  
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Handle different HTML elements
  const processNode = (node) => {
    let text = '';
    
    for (let child of node.childNodes) {
      switch (child.nodeType) {
        case Node.TEXT_NODE:
          text += child.textContent;
          break;
        case Node.ELEMENT_NODE:
          const tagName = child.tagName.toLowerCase();
          
          switch (tagName) {
            case 'br':
              text += '\n';
              break;
            case 'p':
            case 'div':
              text += processNode(child) + '\n\n';
              break;
            case 'h1':
            case 'h2':
            case 'h3':
            case 'h4':
            case 'h5':
            case 'h6':
              text += processNode(child) + '\n\n';
              break;
            case 'ul':
            case 'ol':
              text += '\n' + processNode(child) + '\n';
              break;
            case 'li':
              const listType = child.parentNode.tagName.toLowerCase();
              const prefix = listType === 'ol' ? '• ' : '• ';
              text += prefix + processNode(child) + '\n';
              break;
            case 'blockquote':
              const quoteText = processNode(child);
              text += '\n"' + quoteText + '"\n\n';
              break;
            case 'hr':
              text += '\n---\n\n';
              break;
            case 'a':
              const linkText = processNode(child);
              const href = child.getAttribute('href');
              if (href && href !== linkText) {
                text += `${linkText} (${href})`;
              } else {
                text += linkText;
              }
              break;
            case 'strong':
            case 'b':
              text += '**' + processNode(child) + '**';
              break;
            case 'em':
            case 'i':
              text += '*' + processNode(child) + '*';
              break;
            case 'u':
              text += '_' + processNode(child) + '_';
              break;
            default:
              text += processNode(child);
          }
          break;
      }
    }
    
    return text;
  };
  
  const result = processNode(tempDiv);
  
  // Clean up excessive newlines
  return result
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

/**
 * Convert plain text with markdown-style formatting to HTML
 * @param {string} text - Plain text with markdown
 * @returns {string} HTML string
 */
export const markdownToHtml = (text) => {
  if (!text) return '';
  
  return text
    // Bold
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Italic
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    // Underline
    .replace(/_(.*?)_/g, '<u>$1</u>')
    // Line breaks
    .replace(/\n\n/g, '<br><br>')
    .replace(/\n/g, '<br>')
    // Lists (simple bullet points)
    .replace(/^\• (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .trim();
};

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - HTML string
 * @returns {string} Sanitized HTML string
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  
  // Create a temporary div to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Remove script tags and event handlers
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Remove dangerous attributes
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    const attributes = [...element.attributes];
    attributes.forEach(attr => {
      if (attr.name.startsWith('on') || attr.name === 'javascript:') {
        element.removeAttribute(attr.name);
      }
    });
  });
  
  return tempDiv.innerHTML;
};

/**
 * Format email content for display in the editor
 * @param {string} content - Email content (could be HTML or plain text)
 * @returns {string} Formatted HTML for editor
 */
export const formatForEditor = (content) => {
  if (!content) return '';
  
  // If it's already HTML (contains tags), sanitize and return
  if (content.includes('<') && content.includes('>')) {
    return sanitizeHtml(content);
  }
  
  // If it's plain text, convert to HTML
  return textToHtml(content);
};

/**
 * Format email content for sending
 * @param {string} htmlContent - HTML content from editor
 * @returns {string} Plain text for email sending
 */
export const formatForSending = (htmlContent) => {
  return htmlToText(htmlContent);
};

/**
 * Get plain text preview of email content
 * @param {string} htmlContent - HTML content
 * @param {number} maxLength - Maximum length of preview
 * @returns {string} Plain text preview
 */
export const getTextPreview = (htmlContent, maxLength = 150) => {
  const plainText = htmlToText(htmlContent);
  
  if (plainText.length <= maxLength) {
    return plainText;
  }
  
  return plainText.substring(0, maxLength).trim() + '...';
};

/**
 * Check if content is HTML or plain text
 * @param {string} content - Content to check
 * @returns {boolean} True if HTML, false if plain text
 */
export const isHtml = (content) => {
  if (!content) return false;
  return /<[^>]*>/g.test(content);
};

/**
 * Extract text content from HTML for search/filtering
 * @param {string} html - HTML content
 * @returns {string} Plain text content
 */
export const extractTextContent = (html) => {
  if (!html) return '';
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};
